import { Form } from 'antd';
import { CSSProperties, ReactNode, useEffect, useMemo } from 'react';
import { useLabelPrintingContext } from '..';
import { LabelPrintingRenderCardContentParams } from '../types';

interface Props<T = unknown> {
  item: T;
  index: number;
  renderCardContent: (
    params: LabelPrintingRenderCardContentParams<T>,
  ) => ReactNode;
}

export default function PageContentPaperLabel<T>({
  item,
  index,
  renderCardContent,
}: Props<T>) {
  const { form } = useLabelPrintingContext();
  const values = Form.useWatch([], form);
  const layout = Form.useWatch(['layout'], form);

  const cardId = `print-page-content-card-${index}`;

  const width = layout?.width || 0;
  const paddingInline = layout?.marginInline || 0;
  const gap = layout?.gap || 0;
  const cols = layout?.cols || 1;
  const withCardBorder = !!layout?.withCardBorder;
  const oneCardPerPage = !!layout?.oneCardPerPage;
  const cardHeight = layout?.cardHeight || 0;
  const contentCols = layout?.contentCols || 1;
  const heightAuto = !!layout?.heightAuto;
  const descriptionWithLineClamp = !!layout?.descriptionWithLineClamp;
  const textAlign = layout?.align || 'left';
  const verticalAlign = layout?.verticalAlign || 'flex-start';

  const maxWidth = 800;

  const scale = Math.min(maxWidth / width, 2);

  const cardStyle = useMemo((): CSSProperties => {
    const availableWidth = (width - paddingInline * 2) * scale;
    const cardWidth = (availableWidth - gap * scale * (cols - 1)) / cols;
    const styleWidth = oneCardPerPage ? '100%' : cardWidth + 'px';
    const styleHeight = oneCardPerPage
      ? '100%'
      : heightAuto
      ? 'fit-content'
      : cardHeight * scale + 'px';

    return {
      textAlign: textAlign,
      display: 'flex',
      justifyContent: verticalAlign,
      width: styleWidth,
      height: styleHeight,
    };
  }, [values, layout, cardId]);

  useEffect(() => {
    const card = document.getElementById(cardId);

    if (!card) return;

    // -- CardDescription -------

    const cardDescription = card.querySelector(
      '.label-print-card-description',
    ) as HTMLElement;

    if (cardDescription) {
      cardDescription.style.whiteSpace = !descriptionWithLineClamp
        ? 'pre-wrap'
        : '';
    }

    // -- CardContent -------

    const cardContent = card.querySelector(
      '.label-print-card-content',
    ) as HTMLElement;

    if (cardContent) {
      cardContent.style.justifyContent =
        textAlign === 'left'
          ? 'flex-start'
          : textAlign === 'right'
          ? 'flex-end'
          : 'center';
      cardContent.childNodes.forEach((_node: unknown) => {
        console.log(_node);

        const node = _node as HTMLElement;
        console.log(card.style.width, contentCols);

        node.style.width = `calc((${card.style.width} - 4px * ${contentCols}) / ${contentCols})`;
        node.style.overflow = 'hidden';
      });
    }
  }, [values, layout, cardId]);

  return (
    <div
      className="print-page-content-card"
      id={cardId}
      style={cardStyle}
      data-withBorder={withCardBorder}
    >
      {renderCardContent({
        ...values,
        item,
        node: document.getElementById(cardId),
      })}
    </div>
  );
}
